import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Moment } from 'moment'

import { DocumentTabPane } from '.'
import {
  closeUploadModal,
  openUploadModal,
  useFeatureDispatch,
  useFeatureState,
} from '../lib/state'
import { UploadDocumentType } from '@/constants/businessConstants'

import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { useAnalyticsTrack } from '@/features/amplitude'

import { RowTypes } from '../table/TableRows'

import { MissingStatements } from '../shared/MissingStatements'
import { DocumentSearchBarOnChangeData } from '../shared/DocumentsSearchBar'
import { TabPaneToolbar } from '../shared/TabPaneToolbar'
import { UserDocumentsTable } from '../table'
import { Link, Text } from '@/ui'
import { TabCategoryKeys as ModalTypes } from '../lib/constants'
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'
import { UploadDocumentButton } from '../shared/UploadDocumentButton'

type BankStatementsUrlParams = {
  tabName?: string
  action?: string
}

const COLUMNS = [
  {
    sortBy: 'createdAt',
    title: 'Date Uploaded',
  },
  {
    sortBy: 'fileName',
    title: 'File Name',
  },
  {
    sortBy: 'tag',
    title: 'Statement Month',
  },
  {
    sortBy: 'financialAccount',
    title: 'Financial Account',
  },
  {
    sortBy: null,
    title: 'Actions',
  },
]

export const BankStatements: DocumentTabPane = ({
  documents,
  tableOptions,
}) => {
  const [filteredDocuments, setFilteredDocuments] = useState(documents)
  const { activeUploadModal } = useFeatureState()
  const { tabName, action } = useParams<BankStatementsUrlParams>()
  const location = useLocation()
  const locationState = location.state
  const [monthQuery, setMonthQuery] = useState<number>()
  const [yearQuery, setYearQuery] = useState<number>()
  const [searchParams] = useSearchParams()
  const track = useAnalyticsTrack()
  const dispatch = useFeatureDispatch()

  const onSearch = useCallback((data: DocumentSearchBarOnChangeData) => {
    setFilteredDocuments(data.filtered)
  }, [])

  const openModal = useCallback(() => {
    track('clicked document upload', {
      document_category: ModalTypes.BANK_STATEMENTS,
      document_subcategory: '',
    })

    dispatch?.(openUploadModal(ModalTypes.BANK_STATEMENTS))
  }, [dispatch, track])

  useEffect(() => {
    if (
      locationState?.initiatedBy ===
      GettingStartedOnboardingStep.uploadBankStatements
    ) {
      openModal()
    }
  }, [locationState?.initiatedBy, openModal])

  useEffect(() => {
    setFilteredDocuments(documents)

    if (tabName) {
      const month = Number(searchParams.get('month'))
      const year = Number(searchParams.get('year'))

      if (action === 'upload' && !isNaN(month) && !isNaN(year)) {
        setMonthQuery(month)
        setYearQuery(year)
        openModal()
      }
    }
  }, [documents, action, tabName, location.search, openModal, searchParams])

  const setUploadedFile = useCallback(
    async ({ statementMonth }: { statementMonth?: Moment }) => {
      if (statementMonth) {
        await markUserActionItemCompleteIfExists(
          UserActionItemActionItemIdentifiers.uploadBankStatements({
            month: (statementMonth.month() + 1).toString(),
            year: statementMonth.year().toString(),
          }),
          (event, properties) => track(event, properties)
        )
      }
    },
    [track]
  )

  /**
   * This is the new way of Uploading Docs. Commented out until we migrate to V2
   */
  // const { uploadDocuments, isUploading } = useDocumentUpload({
  //   documentCategory: 'bank_statements',
  //   documentType: UploadDocumentType.STATEMENT,
  //   month: monthQuery,
  //   year: yearQuery?.toString(),
  //   onUploadSuccess: async (_uploadedDocuments) => {
  //     const statementMonth =
  //       monthQuery && yearQuery
  //         ? moment()
  //             .month(monthQuery - 1)
  //             .year(yearQuery)
  //         : undefined

  //     if (statementMonth) {
  //       await setUploadedFile({ statementMonth })
  //     }
  //   },
  //   onUploadError: (errorMessage) => {
  //     console.error('Upload failed:', errorMessage)
  //   },
  // })

  // const handleDocumentsUpload = useCallback(
  //   async (documents: File[]) => {
  //     await uploadDocuments(documents)
  //   },
  //   [uploadDocuments]
  // )

  return (
    <>
      <TabPaneToolbar
        documents={documents}
        onSearch={onSearch}
        // TODO: Update to V2 File Modal skipcq: JS-0099
        rightElement={<UploadDocumentButton onClick={openModal} />}
      >
        <MissingStatements includeUploadButton={false} />

        <Text>
          Share monthly bank and credit card statements here.{' '}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard"
            newPage
          >
            When do I need to upload statements?
          </Link>
        </Text>
      </TabPaneToolbar>

      <UserDocumentsTable
        columns={COLUMNS}
        documents={filteredDocuments}
        rowType={RowTypes.BANK_STATEMENT}
        tableOptions={tableOptions}
      />
      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={activeUploadModal === ModalTypes.BANK_STATEMENTS}
        close={() => dispatch?.(closeUploadModal())}
        userFacing
        monthYear={
          monthQuery && yearQuery ? `${yearQuery}-${monthQuery}` : undefined
        }
        setUploadedFile={setUploadedFile}
      />
    </>
  )
}

BankStatements.paneDescription =
  'Please use this section to upload your monthly business bank account and business credit card statements. We use these to reconcile your books each month and ensure that all of your income and expenses are accounted for. If you have granted our team read-only access to your bank account(s), you do not need to upload statements.'
BankStatements.paneTitle = 'Bank Statement'
